/**
 * Corp-Dossier — Modal overlay showing player/rival intelligence.
 * Opens on click on any player name across all screens.
 */
import { fixture, type CorpStanding, type RivalEntry, type IntelEntry, type NAPEntry } from '../data/fixture';
import { formatCash, nav } from '../main';
import { t } from '../i18n';

// ── Data helpers ────────────────────────────────────────────────────────

function findStanding(corpName: string): CorpStanding | undefined {
  return (fixture.leaderboard ?? []).find(c => c.name === corpName);
}

function findRival(corpName: string): RivalEntry | undefined {
  return (fixture.player?.diplomacy?.rivals ?? []).find(r => r.corp === corpName);
}

function findNap(corpName: string): NAPEntry | undefined {
  return (fixture.player?.diplomacy?.naps ?? []).find(n => n.corp === corpName);
}

function findIntel(corpName: string): IntelEntry[] {
  return (fixture.player?.diplomacy?.intel ?? []).filter(i => i.target === corpName);
}

function isSelf(corpName: string): boolean {
  return (fixture.leaderboard ?? []).some(c => c.name === corpName && c.isMe);
}

// ── Classification level ────────────────────────────────────────────────

type Classification = 'PUBLIC' | 'CLASSIFIED' | 'TOP_SECRET';

function classifyLevel(rival: RivalEntry | undefined, intel: IntelEntry[]): Classification {
  if (rival && intel.length > 0) return 'TOP_SECRET';
  if (rival) return 'CLASSIFIED';
  return 'PUBLIC';
}

function classificationColor(level: Classification): string {
  switch (level) {
    case 'TOP_SECRET': return 'var(--red)';
    case 'CLASSIFIED': return 'var(--amber)';
    case 'PUBLIC': return 'var(--text-lo)';
  }
}

function classificationLabel(level: Classification): string {
  switch (level) {
    case 'TOP_SECRET': return t('dossier.level_top_secret');
    case 'CLASSIFIED': return t('dossier.level_classified');
    case 'PUBLIC': return t('dossier.level_public');
  }
}

// ── Heat bar ────────────────────────────────────────────────────────────

function heatBar(heat: number): string {
  const pct = Math.min(100, Math.max(0, heat));
  const color = pct >= 70 ? 'var(--red)' : pct >= 40 ? 'var(--amber)' : 'var(--green)';
  return `<div style="display:flex;align-items:center;gap:8px">
    <div style="flex:1;height:6px;background:rgba(255,255,255,0.08);border-radius:3px;overflow:hidden">
      <div style="width:${pct}%;height:100%;background:${color};border-radius:3px"></div>
    </div>
    <span style="color:${color};font-family:var(--mono);font-size:12px;min-width:40px;text-align:right">${Math.round(heat)}/100</span>
  </div>`;
}

// ── Scan-line / redacted effect ─────────────────────────────────────────

function redactedRow(label: string): string {
  return `<div style="display:flex;justify-content:space-between;padding:4px 0;opacity:0.3">
    <span>${label}</span>
    <span style="font-family:var(--mono);letter-spacing:2px;color:var(--text-lo)">██████████</span>
  </div>`;
}

// ── Sections ────────────────────────────────────────────────────────────

function publicSection(standing: CorpStanding | undefined, nap: NAPEntry | undefined, selfView: boolean): string {
  const rank = standing ? `#${standing.rank}` : '?';
  const nw = standing ? formatCash(Math.round(standing.netWorth)) : '—';
  const territory = standing ? `${standing.territory}` : '—';
  const eliminated = standing?.eliminated
    ? `<span style="color:var(--red);font-weight:600">${t('dossier.eliminated')}</span>`
    : `<span style="color:var(--green)">${t('dossier.active')}</span>`;

  let napStr: string;
  if (selfView) {
    napStr = `<span style="color:var(--text-lo)">—</span>`;
  } else if (nap) {
    const statusColor = nap.status === 'active' ? 'var(--green)' : 'var(--amber)';
    napStr = `<span style="color:${statusColor}">${nap.status ?? 'active'}${nap.secret ? ` (${t('dossier.nap_secret')})` : ''}</span>`;
  } else {
    napStr = `<span style="color:var(--text-lo)">— ${t('dossier.nap_none')} —</span>`;
  }

  return `<div class="dossier-section">
    <div class="dossier-section-title">${t('dossier.section_public')}</div>
    <div class="dossier-row"><span>${t('dossier.rank')}</span><span style="font-family:var(--mono);color:var(--text-hi)">${rank}</span></div>
    <div class="dossier-row"><span>${t('dossier.net_worth')}</span><span style="font-family:var(--mono);color:var(--green)">${nw}</span></div>
    <div class="dossier-row"><span>${t('dossier.territory')}</span><span style="font-family:var(--mono)">${territory} ${t('dossier.sectors')}</span></div>
    <div class="dossier-row"><span>${t('dossier.status')}</span>${eliminated}</div>
    ${!selfView ? `<div class="dossier-row"><span>${t('dossier.nap_with_you')}</span>${napStr}</div>` : ''}
  </div>`;
}

function intelSection(rival: RivalEntry | undefined, intel: IntelEntry[], selfView: boolean): string {
  if (selfView) {
    // Self-view: show own stats from fixture
    const player = fixture.player;
    const heat = player?.federalHeat ?? 0;
    const runnerCount = player?.runners?.length ?? 0;
    const buildingCount = player?.buildings?.length ?? 0;
    return `<div class="dossier-section">
      <div class="dossier-section-title">${t('dossier.section_own_stats')}</div>
      <div class="dossier-row"><span>${t('dossier.heat')}</span><div style="flex:1;max-width:200px">${heatBar(heat)}</div></div>
      <div class="dossier-row"><span>${t('dossier.runners')}</span><span style="font-family:var(--mono)">${runnerCount}</span></div>
      <div class="dossier-row"><span>${t('dossier.buildings')}</span><span style="font-family:var(--mono)">${buildingCount}</span></div>
    </div>`;
  }

  if (!rival) {
    // No intel at all — fully redacted
    return `<div class="dossier-section dossier-redacted">
      <div class="dossier-section-title">${t('dossier.section_intel')}</div>
      ${redactedRow(t('dossier.heat'))}
      ${redactedRow(t('dossier.dishonored'))}
      <div style="text-align:center;padding:12px 0;color:var(--text-lo);font-size:11px;font-style:italic">
        ${t('dossier.no_intel')}
      </div>
    </div>`;
  }

  // Intel available
  const intelRows = intel.length > 0
    ? intel.map(i => {
      const verified = i.confirmed
        ? `<span style="color:var(--green);font-size:10px">✓ ${t('dossier.intel_confirmed')}</span>`
        : `<span style="color:var(--amber);font-size:10px">? ${t('dossier.intel_unconfirmed')}</span>`;
      return `<div style="padding:6px 0;border-top:1px solid rgba(255,255,255,0.04)">
        <div style="display:flex;justify-content:space-between;align-items:center">
          <span style="color:var(--text-mid);font-size:12px">${i.info}</span>
          ${verified}
        </div>
        <div style="font-size:10px;color:var(--text-lo);margin-top:2px">${t('dossier.intel_cycle')} ${i.cycle} · ${t('dossier.intel_source')}: ${i.source}</div>
      </div>`;
    }).join('')
    : `<div style="color:var(--text-lo);font-size:11px;font-style:italic;padding:4px 0">${t('dossier.no_intel_entries')}</div>`;

  return `<div class="dossier-section">
    <div class="dossier-section-title">${t('dossier.section_intel')}</div>
    <div class="dossier-row"><span>${t('dossier.heat')}</span><div style="flex:1;max-width:200px">${heatBar(rival.heat)}</div></div>
    <div class="dossier-row"><span>${t('dossier.dishonored')}</span><span style="font-family:var(--mono);color:${rival.dishonored > 0 ? 'var(--red)' : 'var(--text-mid)'}">${rival.dishonored}</span></div>
    <div style="margin-top:8px">
      <div style="font-size:11px;color:var(--text-lo);margin-bottom:4px">${t('dossier.intel_reports')} (${intel.length})</div>
      ${intelRows}
    </div>
  </div>`;
}

function actionsSection(corpName: string): string {
  return `<div class="dossier-section dossier-actions">
    <div class="dossier-section-title">${t('dossier.section_actions')}</div>
    <div style="display:flex;gap:8px;flex-wrap:wrap">
      <button class="dossier-action-btn" data-action="nap" data-corp="${corpName}">${t('dossier.action_nap')}</button>
      <button class="dossier-action-btn" data-action="message" data-corp="${corpName}">${t('dossier.action_message')}</button>
      <button class="dossier-action-btn" data-action="spy" data-corp="${corpName}">${t('dossier.action_spy')}</button>
    </div>
  </div>`;
}

// ── Styles ──────────────────────────────────────────────────────────────

const DOSSIER_STYLES = `
  .dossier-overlay {
    position:fixed; inset:0; background:rgba(0,0,0,0.75); z-index:9500;
    display:flex; align-items:center; justify-content:center;
    animation: dossier-fade-in 0.2s ease-out;
  }
  @keyframes dossier-fade-in { from { opacity:0 } to { opacity:1 } }

  .dossier-modal {
    background:var(--surface-modal); backdrop-filter:blur(4px); -webkit-backdrop-filter:blur(4px); border:1px solid var(--line-hi); border-radius:8px;
    width:480px; max-width:92vw; max-height:85vh; overflow-y:auto;
    font-size:13px; color:var(--text-mid);
    animation: dossier-slide-up 0.25s ease-out;
    box-shadow: 0 0 40px rgba(0,0,0,0.5), 0 0 1px rgba(var(--ac),0.1);
  }
  @keyframes dossier-slide-up { from { opacity:0; transform:translateY(20px) } to { opacity:1; transform:translateY(0) } }

  .dossier-header {
    display:flex; gap:14px; align-items:flex-start;
    padding:16px 20px 12px; border-bottom:1px solid var(--border);
  }

  .dossier-avatar {
    width:56px; height:56px; border-radius:50%; object-fit:cover; flex-shrink:0;
    border:2px solid var(--border);
    box-shadow: 0 0 12px rgba(var(--ac),0.08);
  }
  .dossier-avatar-fallback {
    display:flex; align-items:center; justify-content:center;
    background:rgba(var(--ac),0.1); color:var(--cyan);
    font-family:var(--mono); font-size:22px; font-weight:700;
    letter-spacing:1px;
  }
  .dossier-close { background:none; border:none; color:var(--text-lo); font-size:18px; cursor:pointer; padding:0 4px; }
  .dossier-close:hover { color:var(--text-hi); }

  .dossier-corp-name {
    font-family:var(--mono); font-size:18px; font-weight:700; color:var(--text-hi);
    letter-spacing:1px; text-transform:uppercase;
  }
  .dossier-classification {
    font-family:var(--mono); font-size:10px; letter-spacing:2px;
    padding:2px 8px; border:1px solid; border-radius:2px; margin-top:6px; display:inline-block;
  }
  .dossier-label { font-size:10px; color:var(--text-lo); letter-spacing:2px; text-transform:uppercase; margin-top:2px; }

  .dossier-body { padding:4px 20px 20px; }

  .dossier-section { margin-top:16px; }
  .dossier-section-title {
    font-family:var(--mono); font-size:10px; letter-spacing:2px; text-transform:uppercase;
    color:var(--text-lo); padding-bottom:6px; margin-bottom:8px;
    border-bottom:1px solid rgba(255,255,255,0.06);
  }
  .dossier-row {
    display:flex; justify-content:space-between; align-items:center;
    padding:4px 0; gap:12px;
  }

  .dossier-redacted { position:relative; overflow:hidden; }
  .dossier-redacted::after {
    content:''; position:absolute; inset:0; pointer-events:none;
    background: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 2px,
      rgba(var(--ac),0.015) 2px,
      rgba(var(--ac),0.015) 4px
    );
    animation: dossier-scanline 4s linear infinite;
  }
  @keyframes dossier-scanline {
    0% { transform:translateY(0) }
    100% { transform:translateY(40px) }
  }

  .dossier-actions { margin-top:16px; padding-top:12px; border-top:1px solid var(--border); }
  .dossier-action-btn {
    background:rgba(255,255,255,0.04); border:1px solid var(--border); border-radius:4px;
    color:var(--text-mid); font-family:var(--mono); font-size:11px; letter-spacing:0.5px;
    padding:6px 14px; cursor:pointer; transition:all 0.15s;
  }
  .dossier-action-btn:hover {
    background:rgba(var(--ac),0.08); border-color:var(--cyan); color:var(--cyan);
  }

  .dossier-self-badge {
    font-family:var(--mono); font-size:9px; letter-spacing:1px;
    color:var(--cyan); border:1px solid var(--cyan); border-radius:2px;
    padding:1px 6px; margin-left:8px; vertical-align:middle;
  }
`;

// ── Main export ─────────────────────────────────────────────────────────

export function openCorpDossier(corpName: string): void {
  // Remove any existing dossier
  document.querySelector('.dossier-overlay')?.remove();

  const selfView = isSelf(corpName);
  const standing = findStanding(corpName);
  const rival = selfView ? undefined : findRival(corpName);
  const nap = selfView ? undefined : findNap(corpName);
  const intel = selfView ? [] : findIntel(corpName);
  const level = selfView ? 'TOP_SECRET' as Classification : classifyLevel(rival, intel);

  const overlay = document.createElement('div');
  overlay.className = 'dossier-overlay';

  const selfBadge = selfView ? `<span class="dossier-self-badge">${t('dossier.you')}</span>` : '';

  // Player avatar (from leaderboard data)
  const avatarUrl = standing?.avatarUrl;
  const avatarHtml = avatarUrl
    ? `<img class="dossier-avatar" src="${avatarUrl}" alt="${corpName}" loading="lazy">`
    : `<div class="dossier-avatar dossier-avatar-fallback">${corpName.charAt(0).toUpperCase()}</div>`;

  overlay.innerHTML = `
    <div class="dossier-modal">
      <div class="dossier-header">
        ${avatarHtml}
        <div style="flex:1;min-width:0">
          <div class="dossier-label">${t('dossier.title')}</div>
          <div class="dossier-corp-name">${corpName}${selfBadge}</div>
          <div class="dossier-classification" style="color:${classificationColor(level)};border-color:${classificationColor(level)}">
            ${classificationLabel(level)}
          </div>
        </div>
        <button class="dossier-close">\u2715</button>
      </div>
      <div class="dossier-body">
        ${publicSection(standing, nap, selfView)}
        ${intelSection(rival, intel, selfView)}
        ${!selfView ? actionsSection(corpName) : ''}
      </div>
    </div>
  `;

  // Inject styles (once)
  if (!document.getElementById('dossier-styles')) {
    const style = document.createElement('style');
    style.id = 'dossier-styles';
    style.textContent = DOSSIER_STYLES;
    document.head.appendChild(style);
  }

  document.body.appendChild(overlay);

  // Close handlers
  const close = () => overlay.remove();
  overlay.querySelector('.dossier-close')!.addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  // Action button deep-links
  overlay.querySelectorAll('.dossier-action-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const action = (btn as HTMLElement).dataset.action;
      close();
      switch (action) {
        case 'nap':
        case 'message':
          nav('diplomacy');
          break;
        case 'spy':
          nav('map');
          break;
      }
    });
  });

  // ESC to close
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { close(); window.removeEventListener('keydown', onKey); }
  };
  window.addEventListener('keydown', onKey);
}

/**
 * Makes a player name element clickable to open the Corp-Dossier.
 * Call this on any element that displays a corp name.
 */
export function makeCorpClickable(el: HTMLElement, corpName: string): void {
  el.style.cursor = 'pointer';
  el.title = corpName;
  el.addEventListener('click', (e) => {
    e.stopPropagation();
    openCorpDossier(corpName);
  });
}
